import type { ReactNode } from 'react';
import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { v4 as generateUUID } from 'uuid';
import { tokens, Button, Link } from '@hotjar/hotsauce';
import { AnimatePresence, motion } from 'framer-motion';
import { isEmpty, trim } from 'lodash-es';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { GENERIC_OCCURRED_OPTION_LIST } from '../GenericConstants/GenericOccurredOptionList';
import { GenericFilterBody } from '../GenericBloc/GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericBloc/GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericBloc/GenericFilter/GenericFilterFooter';
import { GenericFilterHeader } from '../GenericBloc/GenericFilter/GenericFilterHeader';
import { SubFilterErrorKind } from './ErrorFilterType';
import { COLUMN_ONE_WIDTH } from './subFilter/ErrorFilterConstant';
import { SubFilterWrapper } from './subFilter/SubFilterWrapper';
import { CornerLine } from '../GenericBloc/GenericSubFilter/CornerLine';
import { RadioTextFilterOperation } from '../GenericBloc/GenericRadioTextFilter/GenericRadioTextType';
import { GenericFilterSelect } from '../GenericBloc/GenericFilterSelect/GenericFilterSelect';
import { GenericFilterFooterMessage } from '../GenericBloc/GenericFilter/GenericFilterFooterMessage';
import { isDateRangeBeforeMinDate } from '../DateRangeFilter/DateRangeFilterUtils';
import type { AbstractSingleFilterDropdownProps } from '../SingleFilterType';
import type { ErrorSubFilter, ErrorType } from './ErrorFilterType';
import type { DateRange, MinimalDateConstraint } from '../DateRangeFilter/DateRangeFilterType';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x2};
`;

const OperationWrapper = styled.div`
  width: ${COLUMN_ONE_WIDTH}px;
`;

const AddSubWrapper = styled.div`
  display: flex;
  gap: ${tokens.spacer.x1};
`;

export interface Option<T> {
  value: T;
  label: string;
  content?: ReactNode;
}

const DROPDOWN_WIDTH = 680;
const MAX_SUBFILTER_LENGTH = 10;
const ANIMATION_VARIANTS = {
  enter: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

interface ErrorFilterDropdownProps extends AbstractSingleFilterDropdownProps<ErrorType> {
  dateConstraint?: MinimalDateConstraint;
  dateRange?: DateRange;
}

export function ErrorFilterDropdown({
  currentValue,
  optionalDefaultValue,
  onApply,
  filterName,
  header,
  footer,
  dateConstraint,
  dateRange,
}: ErrorFilterDropdownProps): JSX.Element {
  const helpHotjarUrl = useHelpHotjarUrl();
  const [dirtyValue, setDirtyValue] = useState<ErrorType>(
    currentValue ??
      optionalDefaultValue ?? {
        operation: GENERIC_OCCURRED_OPTION_LIST[0].value,
        subFilters: [],
      },
  );
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const handleOperationChange = useCallback(
    (newValue) => {
      setDirtyValue({ operation: newValue, subFilters: dirtyValue.subFilters });
      setShowValidationError(false);
    },
    [setDirtyValue, setShowValidationError, dirtyValue],
  );

  const addSubFilter = useCallback(() => {
    if (dirtyValue.subFilters.length < MAX_SUBFILTER_LENGTH) {
      setDirtyValue({
        operation: dirtyValue.operation,
        subFilters: [
          ...dirtyValue.subFilters,
          {
            id: generateUUID(),
            kind: SubFilterErrorKind.TEXT,
            value: {
              operation: RadioTextFilterOperation.CONTAINS,
              value: '',
            },
          },
        ],
      });
      setShowValidationError(false);
    }
  }, [setDirtyValue, setShowValidationError, dirtyValue]);

  const removeSubFilter = useCallback(
    (indexToRemove: number) => {
      setDirtyValue({
        operation: dirtyValue.operation,
        subFilters: dirtyValue.subFilters.filter((_item, index) => index !== indexToRemove),
      });
      setShowValidationError(false);
    },
    [setDirtyValue, setShowValidationError, dirtyValue],
  );

  const updateSubFilter = useCallback(
    (indexToUpdate: number, newSubFilter: ErrorSubFilter) => {
      setDirtyValue({
        operation: dirtyValue.operation,
        subFilters: dirtyValue.subFilters.map((item, index) =>
          index === indexToUpdate ? newSubFilter : item,
        ),
      });
      setShowValidationError(false);
    },
    [setDirtyValue, setShowValidationError, dirtyValue],
  );

  const handleApply = useCallback(() => {
    if (
      dirtyValue == null ||
      dirtyValue.subFilters.some(
        (item) => !item.value || !('value' in item.value) || isEmpty(trim(item.value.value)),
      )
    ) {
      setShowValidationError(true);
    } else {
      dirtyValue.subFilters.forEach((item) => {
        if (item.value && 'value' in item.value) {
          // eslint-disable-next-line no-param-reassign
          item.value.value = trim(item.value.value);
        }
      });
      onApply(dirtyValue);
    }
  }, [onApply, dirtyValue]);

  const footerMessage =
    dateRange && dateConstraint && isDateRangeBeforeMinDate(dateRange, dateConstraint) ? (
      <GenericFilterFooterMessage>
        <span>{dateConstraint.reason} </span>
        {dateConstraint.link && (
          <Link kind="inline" href={`${helpHotjarUrl}${dateConstraint.link.href}`} isExternal>
            {dateConstraint.link.text}
          </Link>
        )}
      </GenericFilterFooterMessage>
    ) : (
      (footer !== undefined ? footer : null) || null
    );

  return (
    <GenericFilterDropdown
      ariaLabel={`${filterName} dropdown`}
      onApply={handleApply}
      customWidth={DROPDOWN_WIDTH}
    >
      {header != null && <GenericFilterHeader>{header}</GenericFilterHeader>}
      <GenericFilterBody>
        <Wrapper>
          <OperationWrapper>
            <GenericFilterSelect
              value={dirtyValue.operation}
              onChange={handleOperationChange}
              optionList={GENERIC_OCCURRED_OPTION_LIST}
              size="s"
            />
          </OperationWrapper>

          <AnimatePresence initial={false}>
            {dirtyValue.subFilters.map((item, index) => (
              <motion.div
                layout
                key={item.id}
                variants={ANIMATION_VARIANTS}
                animate="animate"
                exit="exit"
              >
                <SubFilterWrapper
                  value={item}
                  isFirstSubFilter={index === 0}
                  onChange={(newSubFilter) => updateSubFilter(index, newSubFilter)}
                  onDelete={() => removeSubFilter(index)}
                  showValidationError={showValidationError}
                />
              </motion.div>
            ))}
            <motion.div layout key="AddSubFilter">
              <AddSubWrapper>
                <CornerLine />
                <Button
                  kind="ghost"
                  icon="plus"
                  size="s"
                  aria-label="where…"
                  onClick={addSubFilter}
                  isDisabled={dirtyValue.subFilters.length === MAX_SUBFILTER_LENGTH}
                >
                  where…
                </Button>
              </AddSubWrapper>
            </motion.div>
          </AnimatePresence>
        </Wrapper>
      </GenericFilterBody>
      <GenericFilterFooter footerMessage={footerMessage} />
    </GenericFilterDropdown>
  );
}
